// TAŞI — hedef görev seçme penceresi (arama + filtrelenmiş liste).
// Toplu seçimle de çalışır. Döngü kuralı: kendisi ve alt ağacı listelenmez.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, FolderInput, CornerLeftUp, Clock } from "lucide-react";

const LIMIT = 50;

export const MoveTargetModal = ({
  open,
  onClose,
  onPick,          // (targetTaskId | null) → null = ana göreve çıkar
  tasks = [],
  excludeIds = [], // taşınan görevler + alt ağaçları
  recentIds = [],  // SON HEDEFLER — en yeniden eskiye (kullanıcıya özel)
  title = "GÖREVİ TAŞI",
  info = "",
  allowRoot = true,
  busy = false,
}) => {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [limit, setLimit] = useState(LIMIT);
  const kilit = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (open) { setQ(""); setDebounced(""); setLimit(LIMIT); kilit.current = false; }
  }, [open]);

  const haric = useMemo(() => new Set(excludeIds), [excludeIds]);
  const uygunlar = useMemo(
    () => tasks.filter((t) => !haric.has(t.id) && !t.deleted && !t.cancelled),
    [tasks, haric],
  );
  const liste = useMemo(() => {
    if (!debounced) return uygunlar;
    return uygunlar.filter((t) => (t.title || "").toLowerCase().includes(debounced));
  }, [uygunlar, debounced]);

  const sec = (id) => {
    if (kilit.current || busy) return;
    kilit.current = true;
    onPick(id);
  };

  // SON HEDEFLER — silinmiş/erişilemeyen/geçersiz (döngü) hedefler ELENİR.
  const sonHedefler = useMemo(() => {
    if (debounced) return [];
    const harita = new Map(uygunlar.map((t) => [t.id, t]));
    return (recentIds || []).map((id) => harita.get(id)).filter(Boolean).slice(0, 5);
  }, [recentIds, uygunlar, debounced]);

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="move-target-modal"
    >
      <div
        className="w-[min(520px,94vw)] max-h-[82vh] flex flex-col rounded-lg border border-sertex-cyan/40 bg-sertex-surface p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 text-sertex-cyan font-mono mb-1">
          <FolderInput className="h-4 w-4" /> {title}
        </div>
        {info && <div className="text-xs text-sertex-textMuted mb-2">{info}</div>}
        <div className="relative mb-2">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-sertex-textMuted" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Görev ara…"
            data-testid="move-target-search"
            className="w-full pl-7 pr-2 py-1.5 text-xs font-mono bg-black/30 border border-white/15 rounded text-sertex-text focus:border-sertex-cyan/50 outline-none"
          />
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-sertex space-y-1">
          {sonHedefler.length > 0 && (
            <div className="mb-2" data-testid="move-recent-section">
              <div className="hud-text text-sertex-textMuted/80 px-1 pb-1 flex items-center gap-1">
                <Clock className="h-3 w-3" /> SON HEDEFLER
              </div>
              {sonHedefler.map((t) => (
                <button
                  key={`recent-${t.id}`}
                  onClick={() => sec(t.id)}
                  disabled={busy}
                  data-testid={`move-recent-opt-${t.id}`}
                  className="w-full text-left px-3 py-1.5 mb-1 rounded border border-sertex-cyan/25 bg-sertex-cyan/[0.04] hover:border-sertex-cyan/50 hover:bg-sertex-cyan/10 transition-colors disabled:opacity-50"
                >
                  <div className="text-xs font-mono text-sertex-text truncate">{t.title}</div>
                </button>
              ))}
              <div className="h-px bg-white/10 my-2" />
            </div>
          )}
          {allowRoot && (
            <button
              onClick={() => sec(null)}
              disabled={busy}
              data-testid="move-target-root"
              className="w-full flex items-center gap-2 text-left px-3 py-2 rounded border border-sertex-cyan/40 bg-sertex-cyan/10 text-sertex-cyan hover:bg-sertex-cyan/20 transition-colors disabled:opacity-50"
            >
              <CornerLeftUp className="h-4 w-4 shrink-0" />
              <span className="hud-text">ANA GÖREVE ÇIKAR</span>
            </button>
          )}
          {liste.length === 0 ? (
            <div className="hud-text text-sertex-textMuted/70 text-center py-3">
              Uygun hedef görev bulunamadı
            </div>
          ) : (
            liste.slice(0, limit).map((t) => (
              <button
                key={t.id}
                onClick={() => sec(t.id)}
                disabled={busy}
                data-testid={`move-target-opt-${t.id}`}
                className="w-full text-left px-3 py-1.5 rounded border border-white/10 hover:border-sertex-cyan/40 hover:bg-sertex-cyan/5 transition-colors disabled:opacity-50"
              >
                <div className="text-xs font-mono text-sertex-text truncate">{t.title}</div>
                <div className="text-[10px] font-mono text-sertex-textMuted truncate">
                  {[t.company_name, t.assignee_name].filter(Boolean).join(" · ") || "—"}
                </div>
              </button>
            ))
          )}
          {liste.length > limit && (
            <button
              onClick={() => setLimit((v) => v + LIMIT)}
              data-testid="move-target-more"
              className="w-full py-1.5 hud-text text-sertex-textMuted hover:text-sertex-cyan"
            >
              DAHA FAZLA GÖSTER ({liste.length - limit})
            </button>
          )}
        </div>
        <div className="flex justify-end mt-3">
          <button
            onClick={onClose}
            data-testid="move-target-cancel"
            className="hud-text px-3 py-1 rounded border border-white/15 text-sertex-textMuted hover:text-sertex-text"
          >
            Vazgeç
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MoveTargetModal;
